import { useMatomo } from "@jonkoops/matomo-tracker-react";
import { Box, Button, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { APP_DATA_VERSION } from "../../App";
import InstallPopup from "../../Components/IOSPopup";
import bannerImg from "../../images/bannerImg.svg";

const Home = () => {
  const { trackPageView } = useMatomo();
  const [showInstallMessage, setShowInstallMessage] = useState(false);

  useEffect(() => {
    trackPageView({
      documentTitle: "Home",
      href: "/",
    });
  }, [trackPageView]);

  useEffect(() => {
    const savedAppDataVersion = localStorage.getItem(
      "mtx-local-app-data-version",
    );
    const savedSampleData = localStorage.getItem("mtx-local-data-Sample-data");
    // Detects if device is on iOS
    const isIos = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      return /iphone|ipad|ipod/.test(userAgent);
    };
    // Detects if device is in standalone mode
    const isInStandaloneMode = () =>
      "standalone" in window.navigator && window.navigator.standalone;

    // Checks if should display install popup notification:
    if (isIos() && !isInStandaloneMode()) {
      if (
        localStorage.length &&
        localStorage.getItem("mtxpk_installmsg_flag")
      ) {
        const savedInstallMsg = JSON.parse(
          localStorage.getItem("mtxpk_installmsg_flag"),
        );

        const savedInstallMsgTime = new Date(parseInt(savedInstallMsg.date));

        // show dialog only if the setTime is prior to 24 hours from today.
        if (new Date().getTime() - savedInstallMsgTime > 86400000) {
          setShowInstallMessage(true);
        }
      } else {
        setShowInstallMessage(true);
      }
    }
    //preload sample data
    // NOTE: update APP_DATA_VERSION in App.js if you change the sample data
    if (savedAppDataVersion !== APP_DATA_VERSION || !savedSampleData) {
      const sampleData = {
        identifier: "Sample-data",
        sex: "female",
        weight: "30",
        height: "130",
        metricunits: true,
        creatunitmml: false,
        age: "9",
        ageInMonths: false,
        surface: 0,
        bsaCalcUsingMostellerMethod: false,
        indication: "Leukemia",
        dose: 5,
        sim: 0,
        load: 10,
        loadin: 0.5,
        main: 90,
        mainin: 23.5,
        predictioninterval: 95,
        baselinealbumin: false,
        timeInHours: false,
        down: false,
        peff: false,
        parameters: [
          { time: "2022-08-23T13:19", conc: "0", creat: "0.4" },
          { time: "2022-08-24T13:19", conc: "95", creat: "0.52" },
          { time: "2022-08-25T13:19", conc: "0.95", creat: "0.32" },
          { time: "2022-08-26T13:19", conc: "0.43", creat: "0.33" },
          { time: "2022-08-27T13:20", conc: "0.15", creat: "0.33" },
          { time: "2022-08-28T13:20", conc: "0.12", creat: "0.33" },
          { time: "2022-08-29T13:20", conc: "0.08", creat: "0.34" },
        ],
        upperconc: 0.2,
        upperat: 0,
        lowerconc: 0.1,
        lowerat: 0,
        dateSaved: new Date()
          .toJSON()
          .slice(0, 10)
          .split("-")
          .reverse()
          .join("/"),
      };
      localStorage.setItem(
        "mtx-local-data-Sample-data",
        JSON.stringify(sampleData),
      );
      localStorage.setItem("mtx-local-app-data-version", APP_DATA_VERSION);
    }
  }, []);

  // store a flag in localstorage
  const handleInstallMessageClose = () => {
    if (localStorage.length && localStorage.getItem("mtxpk_installmsg_flag")) {
      localStorage.removeItem("mtxpk_installmsg_flag");
    }

    const installMsg = {
      date: new Date().getTime(),
      dontShowInstallMsg: true,
    };

    localStorage.setItem("mtxpk_installmsg_flag", JSON.stringify(installMsg));

    setShowInstallMessage(false);
  };

  return (
    <Grid
      container
      sx={{
        height: "92%",
      }}
    >
      <Grid
        container
        sx={{
          display: "flex",
          backgroundColor: "#4c82cb",
          alignItems: "center",
        }}
      >
        <Grid item sm>
          <Typography
            sx={{
              color: "white",
              fontSize: "x-large",
              fontWeight: 500,
              flex: 1,
              padding: 2.5,
            }}
          >
            MTXPK.org is designed to help clinicians understand the
            pharmacokinetics of high-dose methotrexate, especially with regard
            to delayed clearance.
          </Typography>
        </Grid>
        <Grid
          item
          sm
          sx={{
            height: "100%",
            display: { xs: "none", lg: "block" },
            position: "relative",
          }}
        >
          <Box
            sx={{
              flex: 2,
              position: "relative",
              height: "100%",
            }}
          >
            <img
              alt=""
              src={bannerImg}
              style={{
                position: "absolute",
                bottom: 0,
                margin: "0 auto",
                height: "100%",
              }}
            />
          </Box>
        </Grid>
      </Grid>
      <Grid
        item
        container
        sx={{
          pb: { xs: 0, lg: "50px" },
        }}
      >
        <Grid
          item
          container
          sx={{
            alignItems: "center",
          }}
        >
          <Grid item sm>
            <Box
              sx={{
                width: { xs: "90%", xl: "80%" },
                margin: "0 auto",
                pt: { xs: 3, xl: 0 },
              }}
            >
              <Typography
                variant="h6"
                paragraph
                sx={{
                  color: "#4D82CB",
                  fontWeight: 500,
                }}
              >
                How It Works
              </Typography>
              <Typography variant="body1" paragraph>
                It uses an appropriate pharmacokinetic model for the dose of
                methotrexate to display the concentration vs time curve for an
                individual patient overlaid upon the population-predicted curve
                for that dose.
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  textAlign: "center",
                  pt: 1,
                }}
              >
                <Button
                  sx={{
                    width: "80%",
                    height: 50,
                  }}
                  to="/simulation"
                  variant="contained"
                  size="large"
                  color="primary"
                  component={Link}
                >
                  <Typography variant="h6" sx={{ textTransform: "none" }}>
                    Get Started
                  </Typography>
                </Button>
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            sm
            sx={{
              backgroundColor: "#f6f6f6",
              zIndex: -1,
            }}
          >
            <Box
              sx={{
                width: { xs: "90%", xl: "80%" },
                margin: "0 auto",
                pt: { xs: 3, xl: 0 },
              }}
            >
              <Typography variant="body1" paragraph>
                Required values include the age, sex, height, and weight of the
                patient, the dose and infusion time, at least one methotrexate
                plasma concentration measurement and one creatinine measurement.
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Grid item sm={12}>
          <Box
            sx={{
              margin: "0 auto",
              padding: "0 20px",
            }}
          >
            <Typography
              variant="h6"
              paragraph
              sx={{
                color: "#4D82CB",
              }}
            >
              Notice and Disclaimer
            </Typography>
            <Typography variant="body1" paragraph>
              The User acknowledges and agrees that this tool will be used only
              as a reference aid, and that the information contained in the
              product is not intended to be (nor should it be used as) a
              substitute for the exercise of professional judgement.
            </Typography>
            <Typography variant="body1" paragraph>
              In view of the possibility of human error or changes in medical
              science, the User should confirm the information in the product
              through independent sources.
            </Typography>
            <Typography variant="body1" paragraph>
              MTXPK.org was developed by the Division of Translational and
              Clinical Pharmacology, Cincinnati Children’s Hospital Medical
              Center (CCHMC), Cincinnati, OH, Children's Mercy Hospital (CMH),
              Kansas City, MO, and Medimatics (Maastricht, Netherlands). All
              rights in the tool are reserved by Division of Translational and
              Clinical Pharmacology, Cincinnati Children’s Hospital Medical
              Center, Cincinnati, OH. We do not make any warranty, express, or
              imply, or assume any liability for the use of this tool. Use of
              this website is taken as an agreement to these terms of usage.
              MTXPK.org is free for academic use.
              <br />
              <br />
              The development of MTXPK.org was supported by an unrestricted
              educational grant from BTG International.
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <InstallPopup
        showInstallMessage={showInstallMessage}
        handleInstallMessageClose={handleInstallMessageClose}
      />
    </Grid>
  );
};

export default Home;
